import React, { useEffect, useState } from "react";
import { FaceLivenessDetector } from '@aws-amplify/ui-react-liveness';
import { Credentials } from '@aws-amplify/core';
import goodFitImg from '../assets/liveness-good-fit.jpg';
import tooFarImg from '../assets/liveness-too-far.jpg';

function Liveness({ sessionId, credentials, onAnalysisComplete, onUserCancel, onError }) {
    const region = process.env.REACT_APP_REGION || "us-east-1";
    const [credentialsReady, setCredentialsReady] = useState(false);

    useEffect(() => {
        if (credentials) {
            const formattedCreds = {
                accessKeyId: credentials.accessKeyId,
                secretAccessKey: credentials.secretAccessKey,
                sessionToken: credentials.sessionToken,
                expiration: new Date(credentials.expiration),
                authenticated: true
            };

            // Set global Amplify core Credentials provider so LivenessStreamProvider gets valid STS credentials
            Credentials.get = async () => formattedCreds;
            Credentials._credentials = formattedCreds;
            Credentials._credentials_source = 'session';
            setCredentialsReady(true);
        } else {
            setCredentialsReady(false);
        }
    }, [credentials]);

    const customCredentialsProvider = async () => {
        if (!credentials) {
            throw new Error("Missing temporary streaming credentials for face liveness");
        }
        return {
            accessKeyId: credentials.accessKeyId,
            secretAccessKey: credentials.secretAccessKey,
            sessionToken: credentials.sessionToken,
            expiration: new Date(credentials.expiration)
        };
    };

    const handleError = (error) => {
        console.error("FaceLivenessDetector error:", error);
        if (onError) {
            onError(error);
        }
    };

    if (sessionId && !credentials) {
        return (
            <div className="gi-alert gi-alert-error">
                Temporary credentials could not be obtained for this session. Please request a new verification link.
            </div>
        );
    }

    const CustomHeader = () => (
        <div className="gi-liveness-custom-header">
            <h2 className="gi-title" style={{ fontSize: '1.45rem', marginBottom: '8px' }}>
                Liveness Check
            </h2>
            <p className="gi-subtitle" style={{ fontSize: '0.88rem', margin: '0 auto 10px', maxWidth: '320px', lineHeight: 1.45 }}>
                You will go through a face verification process to prove that you are a real person.
            </p>
        </div>
    );

    const CustomPhotosensitiveWarning = () => (
        <div className="gi-liveness-warning-banner">
            <div className="gi-liveness-warning-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
            </div>
            <div className="gi-liveness-warning-text">
                <strong>Photosensitivity warning</strong>
                <span>This check displays colored flashing lights. Use caution if you are photosensitive.</span>
            </div>
        </div>
    );

    const CustomInstructions = () => (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
            <p className="amplify-text" style={{
                color: 'var(--gi-text-main)',
                fontSize: '0.95rem',
                fontWeight: 700,
                textAlign: 'center',
                letterSpacing: '-0.015em',
                margin: '4px 0 10px 0'
            }}>
                Follow the instructions to complete the check:
            </p>

            <div className="amplify-liveness-figures">
                {/* Good Fit Card */}
                <figure className="amplify-liveness-figure amplify-liveness-figure--success">
                    <div className="amplify-liveness-figure__image amplify-liveness-figure__image--success">
                        <svg className="amplify-liveness-figure__icon" aria-hidden="true" width="24" height="24">
                            <g fill="none">
                                <path fill="#10B981" d="M0 0h24v24H0z" />
                                <path fill="#FFF" d="m9.435 15.62-4.054-4.055L4 12.936l5.435 5.435L21.101 6.704l-1.37-1.371z" />
                            </g>
                        </svg>
                        <img
                            src={goodFitImg}
                            alt="Good fit illustration"
                            style={{ height: '120px', width: 'auto', objectFit: 'contain', display: 'block' }}
                        />
                    </div>
                    <figcaption className="amplify-liveness-figure__caption amplify-liveness-figure__caption--success">
                        Good fit
                    </figcaption>
                </figure>

                {/* Too Far Card */}
                <figure className="amplify-liveness-figure amplify-liveness-figure--error">
                    <div className="amplify-liveness-figure__image amplify-liveness-figure__image--error">
                        <svg className="amplify-liveness-figure__icon" aria-hidden="true" width="24" height="24">
                            <g fill="none">
                                <path fill="#EF4444" d="M0 0h24v24H0z" />
                                <path fill="#FFF" d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                            </g>
                        </svg>
                        <img
                            src={tooFarImg}
                            alt="Too far illustration"
                            style={{ height: '120px', width: 'auto', objectFit: 'contain', display: 'block' }}
                        />
                    </div>
                    <figcaption className="amplify-liveness-figure__caption amplify-liveness-figure__caption--error">
                        Too far
                    </figcaption>
                </figure>
            </div>

            <ol className="amplify-liveness-instruction-list">
                <li><span>1</span><span>Make sure your face is not covered</span></li>
                <li><span>2</span><span>Ensure good lighting and avoid glare</span></li>
                <li><span>3</span><span>Position your face in the oval</span></li>
                <li><span>4</span><span>Hold still during the light scan</span></li>
            </ol>
        </div>
    );

    const livenessDisplayText = {
        instructionsHeaderHeadingText: "Liveness Check",
        instructionsHeaderBodyText: "You will go through a face verification process to prove that you are a real person.",
        photosensitivyWarningHeadingText: "Photosensitivity warning",
        photosensitivyWarningBodyText: "This check displays colored lights. Use caution if you are photosensitive.",
        instructionListHeadingText: "Follow the instructions to complete the check:",
        goodFitCaptionText: "Good fit",
        tooFarCaptionText: "Too far",
        instructionsBeginCheckText: "Begin Check",
        hintMoveFaceFrontOfCameraText: "Position your face in the oval",
        hintTooCloseText: "Move back slightly",
        hintTooFarText: "Move closer to camera",
        hintHoldFacePositionCountdownText: "Hold still for countdown",
        hintConnectingText: "Connecting to secure camera...",
        hintVerifyingText: "Verifying...",
        hintHoldFaceForFreshnessText: "Hold still for color check...",
        timeoutHeaderText: "Verification Timed Out",
        timeoutMessageText: "Your face was not detected in the frame in time. Please center your face in the oval and try again.",
        faceDistanceHeaderText: "Move a Little Closer",
        faceDistanceMessageText: "Please move closer so your face fills the oval.",
        clientHeaderText: "Camera Issue",
        clientMessageText: "Could not connect to your camera properly. Please check your camera permissions and try again.",
        serverHeaderText: "Service Unavailable",
        serverMessageText: "We encountered a temporary connection issue. Please try again in a moment.",
        landscapeHeaderText: "Portrait Mode Required",
        landscapeMessageText: "Please hold your device upright in portrait mode.",
        portraitMessageText: "Keep your device in portrait mode to complete the check.",
        tryAgainText: "Try Again",
    };

    return (
        <div className="gi-liveness-wrapper">
            {sessionId && credentialsReady ? (
                <FaceLivenessDetector
                    sessionId={sessionId}
                    region={region}
                    credentialsProvider={customCredentialsProvider}
                    config={{ credentialsProvider: customCredentialsProvider }}
                    components={{
                        Header: CustomHeader,
                        PhotosensitiveWarning: CustomPhotosensitiveWarning,
                        Instructions: CustomInstructions,
                    }}
                    displayText={livenessDisplayText}
                    onUserCancel={onUserCancel}
                    onAnalysisComplete={onAnalysisComplete}
                    onError={handleError}
                />
            ) : (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    color: '#fef3c7',
                    gap: '12px'
                }}>
                    <div className="gi-spinner" style={{ width: '28px', height: '28px', borderTopColor: '#fbbf24' }} />
                    <span style={{ fontSize: '0.9rem', color: '#9CA3AF' }}>Initializing secure camera...</span>
                </div>
            )}
        </div>
    );
}

export default Liveness;
